import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Modal, Spin, Typography } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { INITIAL_SERVER_SETUP_SIZE_STORAGE_KEY, INITIAL_SERVER_SETUP_STORAGE_KEY } from './NewServerStart';

interface RouteParams {
	saved_server_uuid: string;
}

interface NewServerResponse {
	ok?: boolean;
	task?: string;
	data?: unknown;
	err?: string;
	error?: string;
	payment_not_complete?: boolean;
}

interface NewServerSetupData {
	url_name?: string;
	url_domain?: string;
}

interface ServerActionStateResponse {
	ok?: boolean;
	state?: string;
	msg?: string;
	err?: string;
	error?: string;
}

const SetupServerFinish: React.FC = () => {
	const { saved_server_uuid } = useParams<RouteParams>();
	const history = useHistory();
	const mountedRef = useRef<boolean>(true);
	const [loading, setLoading] = useState<boolean>(true);
	const [statusText, setStatusText] = useState<string>('Starting server setup...');
	const [errorText, setErrorText] = useState<string>('');
	const [isComplete, setIsComplete] = useState<boolean>(false);

	useEffect(() => {
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const normalizeError = (data: { err?: string; error?: string }): string => {
		if (data.err) {
			return data.err;
		}
		if (data.error) {
			return data.error;
		}
		return 'Unknown error';
	};

	const sleep = (milliseconds: number): Promise<void> => {
		return new Promise((resolve) => setTimeout(resolve, milliseconds));
	};

	const getOpenUrl = (rawData: unknown): string | null => {
		let parsed: NewServerSetupData | null = null;

		if (typeof rawData === 'string') {
			try {
				parsed = JSON.parse(rawData) as NewServerSetupData;
			} catch (error) {
				return null;
			}
		} else if (rawData && typeof rawData === 'object') {
			parsed = rawData as NewServerSetupData;
		}

		if (!parsed || !parsed.url_name || !parsed.url_domain) {
			return null;
		}

		return 'https://' + parsed.url_name + '.' + parsed.url_domain;
	};

	useEffect(() => {
		const runSetup = async () => {
			if (!saved_server_uuid) {
				setErrorText('Missing saved server UUID');
				setLoading(false);
				return;
			}

			let createData: NewServerResponse;
			try {
				const createFormData = new FormData();
				createFormData.append('saved_server_uuid', saved_server_uuid);

				const createResp = await fetch('/api/add_server', {
					method: 'POST',
					credentials: 'include',
					body: createFormData,
				});
				createData = await createResp.json();
			} catch (error) {
				if (mountedRef.current) {
					setErrorText('Connection issue while starting server setup');
					setLoading(false);
				}
				return;
			}

			if (!createData.ok || !createData.task) {

				if(createData.payment_not_complete) {
					if (mountedRef.current) {
						setStatusText(normalizeError(createData)+ ". Waiting for payment to complete...");
					}
					await new Promise((resolve) => setTimeout(resolve, 1000));
					runSetup();
					return;
				}

				if (mountedRef.current) {
					setErrorText(normalizeError(createData));
					setLoading(false);
				}
				return;
			}

			const openUrl = getOpenUrl(createData.data);

			localStorage.removeItem(INITIAL_SERVER_SETUP_STORAGE_KEY);
			localStorage.removeItem(INITIAL_SERVER_SETUP_SIZE_STORAGE_KEY);

			setStatusText('Server setup started. Waiting for completion...');

			while (mountedRef.current) {
				let stateData: ServerActionStateResponse;
				try {
					const stateFormData = new FormData();
					stateFormData.append('setup_uuid', createData.task);

					const stateResp = await fetch('/api/server_action_state', {
						method: 'POST',
						credentials: 'include',
						body: stateFormData,
					});
					stateData = await stateResp.json();
				} catch (error) {
					if (mountedRef.current) {
						setErrorText('Connection issue while checking setup state');
						setLoading(false);
					}
					return;
				}

				if (!stateData.ok) {
					if (mountedRef.current) {
						setErrorText(normalizeError(stateData));
						setLoading(false);
					}
					return;
				}

				if (stateData.msg && mountedRef.current) {
					setStatusText(stateData.msg);
				}

				if (stateData.state === 'RUNNING') {
					if (mountedRef.current) {
						setLoading(false);
						setIsComplete(true);
						setStatusText('Server setup complete. Your server is now running.');
						if (openUrl) {
							Modal.confirm({
								title: 'Server is available',
								content: 'Open the new server now?',
								onOk: () => {
									window.open(openUrl, '_blank');
								},
							});
						}
					}
					return;
				}

				if (stateData.state === 'ERROR') {
					if (mountedRef.current) {
						setErrorText(stateData.msg ? stateData.msg : 'Server setup failed');
						setLoading(false);
					}
					return;
				}

				await sleep(1000);
			}
		};

		runSetup();
	}, [saved_server_uuid]);

	return (
		<div
			style={{
				minHeight: '100vh',
				padding: '32px 20px',
				background:
					'radial-gradient(circle at top left, rgba(16, 185, 129, 0.2), transparent 30%), radial-gradient(circle at top right, rgba(14, 165, 233, 0.2), transparent 24%), linear-gradient(180deg, #08151f 0%, #122332 42%, #f4f7fb 42%, #f4f7fb 100%)'
			}}
		>
			<div style={{ maxWidth: 980, margin: '0 auto' }}>
				<div style={{ marginBottom: 24, color: '#eaf3fb' }}>
					<Typography.Text style={{ color: 'rgba(234, 243, 251, 0.7)', textTransform: 'uppercase', letterSpacing: 1.4 }}>
						Server setup
					</Typography.Text>
					<Typography.Title level={1} style={{ color: '#ffffff', marginTop: 8, marginBottom: 8 }}>
						Finishing server creation
					</Typography.Title>
					<Typography.Paragraph style={{ color: 'rgba(234, 243, 251, 0.82)', fontSize: 16, lineHeight: 1.8, marginBottom: 0 }}>
						We are creating your server now. This page updates automatically.
					</Typography.Paragraph>
				</div>

				<Card style={{ borderRadius: 20, boxShadow: '0 24px 80px rgba(3, 11, 21, 0.12)' }}>
					{errorText.length > 0 && <Alert type="error" message={errorText} showIcon style={{ marginBottom: 16 }} />}

					{errorText.length === 0 && loading && (
						<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
							<Spin />
							<Typography.Text>{statusText}</Typography.Text>
						</div>
					)}

					{errorText.length === 0 && !loading && isComplete && (
						<Alert type="success" message={statusText} showIcon style={{ marginBottom: 16 }} />
					)}

					<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
						<Button type="primary" onClick={() => history.push('/app/home')}>
							Go to dashboard
						</Button>
						{errorText.length > 0 && (
							<Button onClick={() => window.location.reload()}>
								Try again
							</Button>
						)}
					</div>
				</Card>
			</div>
		</div>
	);
};

export default SetupServerFinish;
